/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  UiController,
  commands,
  compareWindows,
  consoleCommands,
  events,
} from 'modules/common/ui-controller';
import { getHotelValuesFromList } from 'modules/common/utils/array';
import { KEYS, WORKBOOK_DATE_SELECTION } from 'modules/dashboard/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import { setCompareWindowCommandValue } from 'modules/dashboard/components/tab-container/inner-filter/functions';
import {
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  createVisualCollapseStep,
} from 'modules/common/ui-controller/utils/shared-command-steps';
import { workbookActions } from '../slice';
import { summaryActions } from '../../summary-tab/slice';

const isLoaded = (context) => context.isLoaded;
// a secondary hotel switches the workbook to the hotel comparison visual, this wait lets that visual
// replace the current one so the following commands are applied on the new visual
const COMPARE_HOTEL_DELAY = 4000;
/**
 * Workbook tab filter commands in the order they are applied.
 * The position in this list is the execution order, see useCommandPipeline.
 * context is built in useWorkbookCommandPipeline.
 */
const WORKBOOK_FILTER_COMMAND_STEPS = [
  {
    command: consoleCommands.BOOKING_DATE,
    event: events.SET_WORKBOOK_CUSTOM_BOOKING,
    // open custom booking date picker, the date range is applied once it is opened
    prepare: (value, { dispatch }) => {
      if (value !== commands.OTB) {
        dispatch(workbookActions.setWorkbookOtb(true));
      }
    },
    ready: (context, { pending }) =>
      context.isLoaded &&
      (pending[consoleCommands.BOOKING_DATE] === commands.OTB || Boolean(context.customBooking)),
    apply: (value, { dispatch, resetBookingDate }) => {
      if (value === commands.OTB) {
        dispatch(workbookActions.setWorkbookOtb(false));
        dispatch(workbookActions.setWorkbookBookingDate(resetBookingDate));
      } else {
        // emit set custom booking date range event
        UiController.setBookingDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.DATE_TYPE,
    event: events.SET_WORKBOOK_DATE_TYPE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      // execute dispatch function to set date type dropdown with command value
      const dateType = WORKBOOK_DATE_SELECTION?.filter((type) => type?.id === value);
      if (dateType?.length > 0) {
        dispatch(dashboardActions.setWorkbookDateType(dateType[0]));
      }
    },
  },
  {
    command: consoleCommands.COMPARE_HOTEL,
    event: events.SET_WORKBOOK_COMPARE_HOTELS,
    ready: (context) => context.isLoaded && context.hotelList?.length > 0,
    apply: (value, { dispatch, hotelList }) => {
      const hotelsSelected = getHotelValuesFromList(value, hotelList);
      if (hotelsSelected?.length > 0) {
        dispatch(workbookActions.setEnableSecondaryHotelGroups(false));
        dispatch(workbookActions.setHotelFocusOn(hotelsSelected[0]));
      }
    },
    delayAfter: COMPARE_HOTEL_DELAY,
  },
  {
    command: consoleCommands.COMPARE_HOTEL_GROUP,
    event: events.SET_WORKBOOK_HOTEL_GROUP,
    ready: (context) => context.isLoaded && context.hotelGroupList?.length > 0,
    apply: (value, { dispatch, hotelGroupList }) => {
      const hotelGroupsSelected = getHotelValuesFromList(value, hotelGroupList);
      if (hotelGroupsSelected?.length > 0) {
        dispatch(workbookActions.setEnableSecondaryHotelGroups(true));
        dispatch(workbookActions.setHotelFocusOn(hotelGroupsSelected[0]));
      }
    },
    delayAfter: COMPARE_HOTEL_DELAY,
  },
  {
    command: consoleCommands.ALIGNMENT,
    event: events.SET_WORKBOOK_ALIGNMENT,
    ready: isLoaded,
    apply: (value, { handleToggleChange }) => {
      handleToggleChange({ target: { checked: value } });
    },
  },
  {
    command: consoleCommands.SYMMETRIC,
    event: events.SET_WORKBOOK_SYMMETRIC_COMPARISON,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(workbookActions.setSymmetricComparisonToggle(value === 1));
    },
  },
  {
    command: consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE,
    event: events.SET_WORKBOOK_SYMMETRIC_COMPARISON_COLLAPSE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(summaryActions.setSymmetricComparisonIsDisplay(value === 1));
    },
  },
  {
    command: consoleCommands.RATE_PLAN,
    event: events.SET_WORKBOOK_RATE_PLAN,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(workbookActions.setIsRatePlan(value === 1));
    },
  },
  {
    // custom dates are applied by the next step, only with the custom compare window
    command: consoleCommands.COMPARE_WINDOW,
    requires: [consoleCommands.CUSTOM_DATES],
    event: events.SET_WORKBOOK_COMPARE_WINDOW,
    ready: (context) =>
      context.isLoaded && Boolean(context.dateComparison) && context.dateComparisonList?.length > 0,
    apply: (value, { customBooking, handleComparisonChange }) => {
      setCompareWindowCommandValue(value, customBooking, handleComparisonChange);
      return compareWindows[value] === KEYS.CUSTOM
        ? [consoleCommands.COMPARE_WINDOW]
        : [consoleCommands.COMPARE_WINDOW, consoleCommands.CUSTOM_DATES];
    },
  },
  {
    command: consoleCommands.CUSTOM_DATES,
    event: events.SET_WORKBOOK_CUSTOM_DATE_RANGE,
    ready: (context, { keys }) =>
      context.isLoaded &&
      (context.dateComparison === KEYS.CUSTOM || !keys.includes(consoleCommands.COMPARE_WINDOW)),
    apply: (value, { dispatch, dateComparison }) => {
      if (dateComparison === KEYS.CUSTOM) {
        const datesExtracted = value?.split('-');
        dispatch(workbookActions.setWorkbookCustomDate(datesExtracted[0]));
        // emit event to set custom date range
        UiController.setCustomDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.NORMALIZED_VIEW,
    event: events.SET_WORKBOOK_NORMALIZED_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      // execute dispatch function to set normalized view with command value
      dispatch(workbookActions.setNormalizedView(value === 1));
    },
  },
  {
    command: consoleCommands.IATA,
    event: events.SET_WORKBOOK_IATA,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      // get breakdown option value
      const breakdownOption = parseInt(value?.substring(0, 1), 10);
      // get isDetailed state from command
      const detailsStatus = parseInt(value?.substring(1, 2), 10);
      if (!Number.isNaN(breakdownOption)) {
        dispatch(workbookActions.setIsTravelAgent(breakdownOption === 1));
        dispatch(workbookActions.setIsDetailed(detailsStatus === 1));
      }
    },
  },
  {
    command: consoleCommands.ROOM_NIGHTS_PACE,
    event: events.SET_WORKBOOK_ROOM_NIGHTS_PACE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(workbookActions.setIsRoomNightsPace(value === 1));
    },
  },
  {
    command: consoleCommands.ROOM_TYPE,
    event: events.SET_WORKBOOK_ROOM_TYPE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(workbookActions.setIsRoomType(value === 1));
    },
  },
];

// time the workbook visuals stay hidden before the visual collapse command value is applied
const VISUAL_COLLAPSE_DELAY = 2000;
// actions to show or hide the workbook visuals
const VISUAL_DISPLAY_ACTIONS = {
  D1: workbookActions.setD1SetIsDisplay,
  D2: workbookActions.setD2SetIsDisplay,
  D3: workbookActions.setD3SetIsDisplay,
};
/**
 * Workbook tab commands of a command url in the order they are applied
 */
const WORKBOOK_COMMAND_STEPS = [
  ...WORKBOOK_FILTER_COMMAND_STEPS,
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  createVisualCollapseStep(
    events.SET_WORKBOOK_VISUAL_COLLAPSE,
    VISUAL_COLLAPSE_DELAY,
    () => VISUAL_DISPLAY_ACTIONS
  ),
];

export default WORKBOOK_COMMAND_STEPS;
